import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './History.css';

function History() {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/login');
      return;
    }

    // Verify token
    api.post('/auth/verify', { token }).catch(() => {
      localStorage.removeItem('adminToken');
      navigate('/login');
    });

    // Fetch game history
    fetchGameHistory();
  }, [navigate]);

  const fetchGameHistory = async () => {
    try {
      setLoading(true);
      const response = await api.get('/game/history');
      if (response.data.success) {
        setGames(response.data.games || []);
      }
    } catch (error) {
      console.error('Error fetching game history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'N/A';
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="history-container">
        <div className="history-loading">
          <div className="spinner"></div>
          <p>Loading game history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="history-container">
      <header className="history-header">
        <div className="history-header-content">
          <button onClick={handleBackToDashboard} className="back-btn">
            ← Back to Dashboard
          </button>
          <h1>📊 Game History</h1>
        </div>
      </header>

      <div className="history-content">
        {games.length === 0 ? (
          <div className="no-games">
            <p>No game history available yet.</p>
          </div>
        ) : (
          <div className="games-list">
            {games.map((game, index) => (
              <div key={game._id || index} className="game-card">
                <div className="game-card-header">
                  <h2>Game #{games.length - index}</h2>
                  <div className="game-meta">
                    <span className="game-date">
                      {formatDate(game.startTime)}
                    </span>
                    {game.endTime && (
                      <span className="game-duration">
                        Duration: {formatDuration(game.startTime, game.endTime)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="game-teams">
                  {game.teamProgress && game.teamProgress.length > 0 ? (
                    game.teamProgress.map((team, teamIndex) => (
                      <div key={teamIndex} className="team-scorecard">
                        <h3 className="team-name">{team.teamName || `Team ${teamIndex + 1}`}</h3>
                        
                        <div className="score-summary">
                          <div className="total-score">
                            <span className="score-label">Total Score:</span>
                            <span className="score-value">{team.totalScore || 0}</span>
                          </div>
                        </div>

                        <div className="section-scores">
                          <h4>Section Scores:</h4>
                          <div className="sections-grid">
                            {Object.entries(team.sections || {}).map(([section, data]) => (
                              <div key={section} className="section-score-item">
                                <span className="section-name">
                                  {section.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                                <span className="section-score-value">
                                  {data.score || 0}
                                </span>
                                {data.completed && (
                                  <span className="completed-badge">✓</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Show final game scores if they exist (game completed with final game) */}
                        {game.finalGameScores && game.finalGameScores.length > 0 && (
                          <div className="final-game-score">
                            {game.finalGameScores.find(fs => 
                              fs.teamId?.toString() === team.teamId?.toString()
                            ) && (
                              <div className="final-score-item">
                                <span className="final-score-label">Final Game Score:</span>
                                <span className="final-score-value">
                                  {game.finalGameScores.find(fs => 
                                    fs.teamId?.toString() === team.teamId?.toString()
                                  )?.score || 0}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="no-teams">No teams participated in this game.</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default History;

